import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { BlueprintService } from 'src/app/template-editor/services/blueprint.service';
import { BrandingService } from 'src/app/template-editor/template-components/services/branding.service';

export interface PresentationData {
  presentationId: string;
  presentation?: any;
  blueprintData?: any;
  brandingSettings?: any;
  loadingState: {
    presentation: boolean;
    blueprint: boolean;
    branding: boolean;
  };
  errors: {
    presentation?: any;
    blueprint?: any;
    branding?: any;
  };
  isFullyLoaded: boolean;
}

export interface PresentationLoadResult {
  success: boolean;
  data?: PresentationData;
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PresentationManagerService {

  private presentations = new Map<string, PresentationData>();
  private presentationsSubject = new BehaviorSubject<Map<string, PresentationData>>(new Map());

  private readonly MAX_CACHE_SIZE = 10;

  public presentations$ = this.presentationsSubject.asObservable();

  constructor(
    private presentationApiService: PresentationApiService,
    private blueprintService: BlueprintService,
    private brandingService: BrandingService
  ) { }

  getPresentationData(presentationId: string): PresentationData | null {
    return this.presentations.get(presentationId) || null;
  }

  isPresentationFullyLoaded(presentationId: string): boolean {
    const data = this.presentations.get(presentationId);
    return data?.isFullyLoaded || false;
  }

  async loadPresentation(presentationId: string, forceReload = false): Promise<PresentationLoadResult> {
    const existing = this.presentations.get(presentationId);
    if (existing?.isFullyLoaded && !forceReload) {
      return { success: true, data: existing };
    }

    this._manageCacheSize();

    const presentationData: PresentationData = existing || {
      presentationId,
      loadingState: {
        presentation: false,
        blueprint: false,
        branding: false
      },
      errors: {},
      isFullyLoaded: false
    };

    this.presentations.set(presentationId, presentationData);
    this._notifyUpdate();

    await this._loadPresentationData(presentationData);

    if (presentationData.errors.presentation) {
      return { success: false, error: presentationData.errors.presentation };
    }

    if (presentationData.presentation?.productCode) {
      await this._loadBlueprintData(presentationData);
    }

    await this._loadBrandingData(presentationData);

    presentationData.isFullyLoaded = !presentationData.errors.presentation &&
                                      !presentationData.errors.blueprint &&
                                      !presentationData.errors.branding;

    this._notifyUpdate();

    return { success: true, data: presentationData };
  }

  async loadPresentations(presentationIds: string[]): Promise<PresentationLoadResult[]> {
    const loadPromises = presentationIds.map(id => this.loadPresentation(id));
    return Promise.all(loadPromises);
  }

  clearPresentation(presentationId: string): void {
    this.presentations.delete(presentationId);
    this._notifyUpdate();
  }

  clearAllPresentations(): void {
    this.presentations.clear();
    this._notifyUpdate();
  }

  getPresentationData$(presentationId: string): Observable<PresentationData | null> {
    return this.presentations$.pipe(
      map(presentations => presentations.get(presentationId) || null)
    );
  }

  private async _loadPresentationData(presentationData: PresentationData): Promise<void> {
    presentationData.loadingState.presentation = true;
    delete presentationData.errors.presentation;
    this._notifyUpdate();

    try {
      if (!presentationData.presentationId) {
        throw new Error('Presentation ID is required');
      }

      const result = await this.presentationApiService.get(presentationData.presentationId);

      if (!result || !result.item) {
        throw new Error('Invalid presentation data received from API');
      }

      presentationData.presentation = result.item;

      if (presentationData.presentation.templateAttributeData &&
          typeof presentationData.presentation.templateAttributeData === 'string') {
        try {
          presentationData.presentation.templateAttributeData =
            JSON.parse(presentationData.presentation.templateAttributeData);
        } catch (e) {
          console.warn('Failed to parse templateAttributeData JSON:', e);
          presentationData.presentation.templateAttributeData = {};
        }
      } else if (!presentationData.presentation.templateAttributeData) {
        presentationData.presentation.templateAttributeData = {};
      }
    } catch (error) {
      presentationData.errors.presentation = error;
      console.error('Failed to load presentation data:', error);
    } finally {
      presentationData.loadingState.presentation = false;
      this._notifyUpdate();
    }
  }

  private async _loadBlueprintData(presentationData: PresentationData): Promise<void> {
    presentationData.loadingState.blueprint = true;
    delete presentationData.errors.blueprint;
    this._notifyUpdate();

    try {
      const productCode = presentationData.presentation?.productCode;

      await this.blueprintService.getBlueprintCached(productCode);
      presentationData.blueprintData = this.blueprintService.blueprintData;

      if (!presentationData.blueprintData) {
        throw new Error('Failed to load blueprint data');
      }
    } catch (error) {
      presentationData.errors.blueprint = error;
      console.error('Failed to load blueprint data:', error);
    } finally {
      presentationData.loadingState.blueprint = false;
      this._notifyUpdate();
    }
  }

  private async _loadBrandingData(presentationData: PresentationData): Promise<void> {
    presentationData.loadingState.branding = true;
    delete presentationData.errors.branding;
    this._notifyUpdate();

    try {
      this.brandingService.getBrandingComponent();
      presentationData.brandingSettings = this.brandingService.brandingSettings;
    } catch (error) {
      presentationData.errors.branding = error;
      console.error('Failed to load branding data:', error);
    } finally {
      presentationData.loadingState.branding = false;
      this._notifyUpdate();
    }
  }

  private _notifyUpdate(): void {
    this.presentationsSubject.next(new Map(this.presentations));
  }



  async ensurePresentationLoaded(presentationId: string, forceReload = false): Promise<PresentationData | null> {
    const result = await this.loadPresentation(presentationId, forceReload);
    return result.success ? result.data : null;
  }

  async ensurePresentationsLoaded(presentationIds: string[]): Promise<(PresentationData | null)[]> {
    const results = await this.loadPresentations(presentationIds);
    return results.map(result => result.success ? result.data : null);
  }

  private _manageCacheSize(): void {
    if (this.presentations.size >= this.MAX_CACHE_SIZE) {
      const oldestPresentationId = Array.from(this.presentations.keys())[0];
      this.presentations.delete(oldestPresentationId);
      console.warn(`Cache overflow. Removing oldest presentation: ${oldestPresentationId}`);
    }
  }
}
